class CartRecommendations extends HTMLElement {
  connectedCallback() {
    this.inDrawer = !!this.closest('#CartDrawer');
    this.loadedFor = null;
    this.performRecommendations();

    if (!this.inDrawer) return;

    // Off the product page the drawer has no product of its own, so it
    // recommends for the first item in the cart, refreshed when the
    // drawer opens or the cart changes.
    this.refresh = this.refresh.bind(this);
    document.addEventListener('drawerOpen.CartDrawer', this.refresh);
    // The row has no width while the drawer is closed, so measure the
    // arrows again once it is open.
    document.addEventListener('drawerOpen.CartDrawer', () => {
      requestAnimationFrame(() => this.updateArrows());
    });
    document.addEventListener('cart:updated', this.refresh);
    document.addEventListener('ajaxProduct:added', this.refresh);

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('scroll', this.updateArrows.bind(this), { capture: true, passive: true });
    // Its stylesheet loads deferred; re-measure whenever the row's size
    // changes (stylesheet applied, cards swapped in, drawer resized).
    if (window.ResizeObserver) {
      new ResizeObserver(() => this.updateArrows()).observe(this);
    } else {
      window.addEventListener('resize', this.updateArrows.bind(this));
    }
  }

  performRecommendations() {
    const recommendations = this.querySelector('[data-recommendations]');
    if (!recommendations) return;

    // Outside a product page the drawer renders with an empty product_id,
    // which /recommendations/products answers with a 422.
    const url = new URL(this.dataset.url, window.location.origin);
    const productId = url.searchParams.get('product_id');
    if (!productId) {
      if (this.inDrawer) this.refresh();
      return;
    }
    this.load(url, productId);
  }

  refresh() {
    if (this.refreshing) return;
    this.refreshing = true;
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    fetch(root + 'cart.js', { credentials: 'same-origin' })
      .then(r => r.json())
      .then(cart => {
        this.refreshing = false;
        const pageProduct = new URL(this.dataset.url, window.location.origin).searchParams.get('product_id');
        const ids = cart.items.map(item => String(item.product_id));
        if (!ids.length && !pageProduct) {
          this.classList.add('hidden');
          return;
        }
        const productId = pageProduct || ids[0];
        // Same anchor product and same cart: nothing to redo.
        const key = productId + ':' + ids.join(',');
        if (key === this.loadedFor) {
          this.updateArrows();
          return;
        }
        const url = new URL(this.dataset.drawerUrl, window.location.origin);
        url.searchParams.set('product_id', productId);
        this.load(url, productId, key);
      })
      .catch(() => { this.refreshing = false; });
  }

  load(url, productId, key) {
    const recommendations = this.querySelector('[data-recommendations]');
    fetch(url)
      .then(response => (response.ok ? response.text() : ''))
      .then(text => {
        if (!text) return;
        const source = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('[data-recommendations]');
        const html = source ? source.innerHTML : '';
        if (!html) return;
        this.loadedFor = key || null;
        recommendations.innerHTML = html;
        // The drawer row drops products already in the cart; hide it when
        // nothing is left.
        const empty = this.inDrawer && !recommendations.querySelector('.iwa-cr__card');
        this.classList.toggle('hidden', empty);
        if (this.inDrawer) {
          const track = this.querySelector('[data-cr-track]');
          if (track) track.scrollLeft = 0;
          this.updateArrows();
        }
        this.dispatchEvent(new CustomEvent('recommendations:loaded', { bubbles: true }));
      });
  }

  updateArrows() {
    const track = this.querySelector('[data-cr-track]');
    if (!track) return;
    if (!track.clientWidth) return;
    const max = track.scrollWidth - track.clientWidth;
    const pos = Math.abs(track.scrollLeft);
    this.classList.toggle('iwa-cr--fits', max <= 2);
    const prev = this.querySelector('[data-cr-prev]');
    const next = this.querySelector('[data-cr-next]');
    if (prev) prev.disabled = pos <= 2;
    if (next) next.disabled = pos >= max - 2;
  }

  onClick(e) {
    const arrow = e.target.closest('[data-cr-prev], [data-cr-next]');
    if (arrow) {
      const track = this.querySelector('[data-cr-track]');
      const card = track && track.querySelector('.iwa-cr__card');
      if (!card) return;
      const dir = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
      const step = arrow.hasAttribute('data-cr-next') ? 1 : -1;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      track.scrollBy({ left: step * dir * (card.offsetWidth + gap), behavior: 'smooth' });
      return;
    }

    const add = e.target.closest('[data-cr-add]');
    if (add) {
      this.addVariant(add);
      return;
    }

    const quick = e.target.closest('[data-cr-quick-view]');
    if (quick) {
      this.openQuickView(quick, quick.dataset.crQuickView);
      return;
    }

    // Photo or title: quick view on a plain click; modifier clicks and
    // middle clicks still open the product page.
    const link = e.target.closest('.iwa-cr__media, .iwa-cr__name');
    const card = link && link.closest('[data-cr-handle]');
    if (!card || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    this.openQuickView(link, card.dataset.crHandle);
  }

  addVariant(button) {
    if (button.disabled) return;
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    button.disabled = true;
    button.classList.add('is-loading');
    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(button.dataset.crAdd), quantity: 1 }],
        sections: (window.theme && theme.cart && theme.cart.drawerSection) || 'cart-ajax'
      })
    })
      .then(r => r.json().then(data => { if (!r.ok) throw data; return data; }))
      .then(data => {
        const card = button.closest('.iwa-cr__card');
        if (card) card.remove();
        // The theme's cart form rebuilds the items, subtotal and counts from
        // the drawer section that came back with the add.
        document.dispatchEvent(new CustomEvent('cart:build', { detail: { sections: data.sections } }));
        this.refresh();
      })
      .catch(data => {
        button.disabled = false;
        button.classList.remove('is-loading');
        window.alert((data && (data.description || data.message)) || this.dataset.addError);
      });
  }

  openQuickView(button, handle) {
    const go = () => {
      // The quick view opens over the drawer, which stays open behind it:
      // the drawer ignores clicks inside .iwa-qv (theme.js) and the Escape
      // that closes the dialog (quick-view.js). Adding from the quick view
      // fires ajaxProduct:added, which refills the open drawer in place,
      // and closing hands focus back to this card.
      window.iwaQuickView.open(handle, button);
    };
    if (window.iwaQuickView) return go();

    button.classList.add('is-loading');
    if (!document.querySelector('link[data-iwa-qv-css], link[href*="quick-view.css"]')) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = this.dataset.qvCss;
      css.dataset.iwaQvCss = '';
      document.head.appendChild(css);
    }
    const script = document.createElement('script');
    script.src = this.dataset.qvJs;
    script.onload = () => {
      button.classList.remove('is-loading');
      if (window.iwaQuickView) go();
    };
    script.onerror = () => {
      button.classList.remove('is-loading');
      const link = button.closest('.iwa-cr__card').querySelector('.iwa-cr__name');
      if (link) window.location = link.href;
    };
    document.head.appendChild(script);
  }
}

customElements.define('cart-recommendations', CartRecommendations);
